import { useState } from "react";
import { toast } from "sonner";
import { Share2, MessageCircle, MessageSquare, Link2, ImageDown, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { createShare, formatApiError } from "@/lib/api";
import { shareUrl, summaryText, renderCard } from "@/lib/share";
import { useAuth } from "@/context/AuthContext";

const btn =
  "flex items-center gap-2 justify-center border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-[#f8fafc] px-3 py-3 mono text-xs uppercase tracking-widest transition-colors";

export function ShareButton({ getPayload }) {
  const { isAuthed, setShowAuth } = useAuth();
  const [share, setShare] = useState(null);
  const [cacheKey, setCacheKey] = useState("");
  const [busy, setBusy] = useState(false);

  const open = async () => {
    if (!isAuthed) {
      setShowAuth(true);
      return;
    }
    const full = getPayload();
    if (!full) {
      toast.error("Run a calculation first");
      return;
    }
    // Large calculators (tally) provide a compact share payload.
    const p = full.share ? { ...full, ...full.share } : full;
    const key = JSON.stringify([p.calculator, p.inputs, p.outputs]);
    if (share && key === cacheKey) return setShare({ ...share });
    setBusy(true);
    try {
      const s = await createShare({
        title: p.default_name || p.category, category: p.category, calculator: p.calculator,
        inputs: p.inputs, outputs: p.outputs, units: p.units || "field",
      });
      setShare(s);
      setCacheKey(key);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not create share link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="share-calc-btn"
        onClick={open}
        disabled={busy}
        className="inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-[#94a3b8] px-3 py-2 mono text-xs uppercase tracking-widest transition-colors disabled:opacity-40"
      >
        <Share2 className="w-3.5 h-3.5" /> {busy ? "…" : "Share"}
      </button>
      {share && <ShareDialog share={share} onClose={() => setShare(null)} />}
    </>
  );
}

export function ShareDialog({ share, onClose }) {
  const text = summaryText(share);
  const url = shareUrl(share.id);
  const enc = encodeURIComponent(text);

  const copy = async () => {
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Link copied");
  };

  const downloadImage = async () => {
    const blob = await renderCard(share);
    const file = new File([blob], `rigcalc-${share.calculator}.png`, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: share.title, text });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Image card downloaded");
  };

  const nativeShare = () => navigator.share({ title: share.title, text, url }).catch(() => {});

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="share-dialog" className="bg-[#11151c] border border-[#334155] rounded-none text-[#f8fafc] max-w-md">
        <DialogHeader>
          <DialogTitle className="heading uppercase tracking-wide">Share with your crew</DialogTitle>
          <DialogDescription className="text-[#94a3b8] text-xs">Anyone with the link can view this result — no account needed.</DialogDescription>
        </DialogHeader>
        <pre data-testid="share-preview-text" className="mono text-[11px] whitespace-pre-wrap break-all bg-[#0b0e13] border border-[#334155] p-3 text-[#cbd5e1] max-h-48 overflow-auto">{text}</pre>
        <div className="grid grid-cols-2 gap-2">
          <a data-testid="share-whatsapp" className={btn} href={`https://wa.me/?text=${enc}`} target="_blank" rel="noreferrer">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
          <a data-testid="share-sms" className={btn} href={`sms:?&body=${enc}`}>
            <MessageSquare className="w-4 h-4" /> Text
          </a>
          <button data-testid="share-copy-link" className={btn} onClick={copy}>
            <Link2 className="w-4 h-4" /> Copy link
          </button>
          <button data-testid="share-image" className={btn} onClick={downloadImage}>
            <ImageDown className="w-4 h-4" /> Image card
          </button>
        </div>
        {typeof navigator !== "undefined" && navigator.share && (
          <button data-testid="share-native" onClick={nativeShare} className="flex items-center gap-2 justify-center bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] py-3 mono text-xs uppercase tracking-widest font-bold transition-colors">
            <Send className="w-4 h-4" /> More options
          </button>
        )}
        <a data-testid="share-open-link" href={`/r/${share.id}`} target="_blank" rel="noreferrer" className="text-[11px] mono text-[#64748b] hover:text-[#f59e0b] break-all">{url}</a>
      </DialogContent>
    </Dialog>
  );
}
